import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBolt,
  faCalculator,
  faCircleInfo,
  faCopy,
  faEyeSlash,
  faFolderTree,
  faRocket,
  faTruck,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { Popover } from "@mui/material";
import { Role } from "../../../../core/models/entities/Role";
import { WhatsNewEntry } from "../../../../core/models/entities/WhatsNewEntry";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";

const LAST_SEEN_KEY = "kitmaker.whatsNewLastSeen";

// The API sends icon NAMES; unknown names fall back to a generic info icon
// so a new backend icon never breaks older frontends.
const ICONS: Record<string, IconDefinition> = {
  folders: faFolderTree,
  bolt: faBolt,
  calculator: faCalculator,
  copy: faCopy,
  rocket: faRocket,
  sparkles: faWandMagicSparkles,
  eye: faEyeSlash,
  truck: faTruck,
};

const MONTH_NAMES = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];

// "2026-07-09" -> "JULY 2026". Parsed from the string parts (not Date) so
// timezones can never shift an entry into the neighbouring month.
function monthLabel(date: string): string {
  const [year, month] = date.split("-");
  const name = MONTH_NAMES[Number(month) - 1];
  return name ? `${name} ${year}` : date;
}

interface MonthGroup {
  label: string;
  entries: WhatsNewEntry[];
}

export function WhatsNewMenu(): JSX.Element | null {
  const { contentRepository } = useRepositoriesContext();
  const { role } = useProfileContext();
  const { data: entries } = contentRepository.useWhatsNew();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [lastSeen, setLastSeen] = useState<string>(
    () => localStorage.getItem(LAST_SEEN_KEY) ?? "",
  );

  // Vendor-audience entries are only for vendors and admins; customers
  // (and a not-yet-loaded role) only see "all" entries.
  const canSeeVendorEntries = role === Role.Vendor || role === Role.Admin;
  const visibleEntries = useMemo(
    () =>
      (entries ?? []).filter(
        (entry) => entry.audience !== "vendor" || canSeeVendorEntries,
      ),
    [entries, canSeeVendorEntries],
  );

  // Preserves the API's newest-first order; a group closes when the month
  // changes.
  const groups = useMemo(() => {
    const result: MonthGroup[] = [];
    for (const entry of visibleEntries) {
      const label = monthLabel(entry.date);
      const last = result[result.length - 1];
      if (last && last.label === label) last.entries.push(entry);
      else result.push({ label, entries: [entry] });
    }
    return result;
  }, [visibleEntries]);

  // Nothing to announce (or fetch failed silently): no button at all.
  if (visibleEntries.length === 0) return null;

  // ISO dates compare correctly as strings; max-reduce instead of [0] so a
  // mis-sorted payload can never freeze the unread dot.
  const newestDate = visibleEntries.reduce(
    (max, entry) => (entry.date > max ? entry.date : max),
    "",
  );
  const hasUnread = newestDate > lastSeen;
  const open = Boolean(anchorEl);

  function isUnread(entry: WhatsNewEntry): boolean {
    return entry.date > lastSeen;
  }

  function markAllAsRead() {
    localStorage.setItem(LAST_SEEN_KEY, newestDate);
    setLastSeen(newestDate);
  }

  function handleClose() {
    // Closing the panel counts as having seen everything in it.
    markAllAsRead();
    setAnchorEl(null);
  }

  return (
    // Lives in the top contact strip: inherits its uppercase text-xxs styling.
    <span className="relative inline-flex">
      <button
        aria-label="What's new"
        title="What's new"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        className="relative inline-flex items-center gap-1.5 uppercase hover:opacity-70"
      >
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        <span>What&apos;s new</span>
        {hasUnread && (
          <span className="absolute -top-1 -right-2 h-1.5 w-1.5 rounded-full bg-red-500" />
        )}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className="flex flex-col max-h-[400px] w-[380px] max-w-[calc(100vw-2rem)]">
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-200">
            <span className="text-sm font-bold text-gray-800">
              What&apos;s new in KitMaker
            </span>
            <button
              onClick={markAllAsRead}
              className="text-xs font-medium text-emerald-600 hover:underline whitespace-nowrap"
            >
              Mark all as read
            </button>
          </div>
          <div className="overflow-y-auto pb-2">
            {groups.map((group) => (
              <div key={group.label}>
                <div className="px-4 pt-3 pb-1 text-xxs font-bold uppercase tracking-wider text-gray-400">
                  {group.label}
                </div>
                {group.entries.map((entry) => (
                  <div
                    key={entry.id}
                    className={`flex gap-3 px-4 py-2.5 border-l-2 ${
                      isUnread(entry)
                        ? "border-emerald-500"
                        : "border-gray-200"
                    }`}
                  >
                    <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                      <FontAwesomeIcon icon={ICONS[entry.icon] ?? faCircleInfo} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">
                          {entry.title}
                        </span>
                        {isUnread(entry) && (
                          <span className="shrink-0 rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-px text-[10px] font-bold uppercase">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {entry.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Popover>
    </span>
  );
}
